// Cookie consent banner — GDPR + India DPDP Act 2023 compliance.
// Lightweight, single-purpose. Only shown until the user makes a choice.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const KEY = "pintaflow_cookie_consent";

export function getCookieConsent() {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only if user hasn't decided yet. Delay a beat so it doesn't fight
    // hero animations on first paint.
    const t = setTimeout(() => {
      if (!getCookieConsent()) setVisible(true);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const decide = (choice) => {
    try { localStorage.setItem(KEY, choice); } catch { /* noop */ }
    // Inform analytics so they can opt-out (PostHog respects this flag).
    if (choice === "essential" && typeof window !== "undefined" && window.posthog) {
      try { window.posthog.opt_out_capturing(); } catch { /* noop */ }
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed left-3 right-3 md:left-6 md:right-auto md:max-w-md bottom-3 md:bottom-6 z-[120] rounded-2xl p-4 md:p-5 shadow-2xl"
      style={{
        background: "var(--surface)",
        border: "1px solid rgba(216,168,87,0.30)",
        boxShadow: "0 20px 50px rgba(0,0,0,0.55)",
      }}
      data-testid="cookie-banner"
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(216,168,87,0.10)", border: "1px solid rgba(216,168,87,0.30)" }}
        >
          <Cookie size={16} style={{ color: "var(--seal)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-base md:text-lg" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>
            A small note on cookies
          </div>
          <p className="text-xs mt-1.5 leading-relaxed" style={{ color: "var(--ink-72)" }}>
            We use a privacy-friendly analytics tool (PostHog) to understand which
            features creators love. No ads, no selling data. Choose what's ok with you.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button
              onClick={() => decide("all")}
              className="btn-primary text-xs"
              data-testid="cookie-accept-all"
              style={{ touchAction: "manipulation" }}
            >
              Accept all
            </button>
            <button
              onClick={() => decide("essential")}
              className="btn-secondary text-xs"
              data-testid="cookie-essential-only"
              style={{ touchAction: "manipulation" }}
            >
              Essential only
            </button>
          </div>
          <div className="text-[10px] mt-3" style={{ color: "var(--ink-55)" }}>
            <Link to="/privacy" className="underline hover:opacity-80">Privacy policy</Link>
            {" · "}
            <Link to="/terms" className="underline hover:opacity-80">Terms</Link>
          </div>
        </div>
        <button
          onClick={() => decide("essential")}
          className="text-xs flex-shrink-0 -mr-1 -mt-1"
          style={{ color: "var(--ink-55)", touchAction: "manipulation" }}
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
